import React, { useEffect, useState } from "react";
import afterPaint from "./afterPaint";

// The marketing site does not scroll the window: <main className="routeScroll">
// is a fixed-height overflow container under the fixed header (index.css
// .routeScroll), so a window scroll listener would never fire. That element is
// rendered once in App and never remounts, so it is looked up once.
const THRESHOLD = 480;

// Fixed elements are positioned from the VIEWPORT edge, but the scrollbar lives
// inside the scroll container, so a plain `right: 22px` crowds it (measured on
// the marketing site: a 15px scrollbar left a 7px gap). Publish the real gutter
// as --scroll-gutter. It is re-measured on scroll because async content can
// make the scrollbar appear after mount; overlay scrollbars (macOS, mobile)
// correctly measure 0.
let lastGutter = null;
function syncScrollGutter(el) {
  const gutter = Math.max(0, el.offsetWidth - el.clientWidth);
  if (gutter === lastGutter) return;
  lastGutter = gutter;
  document.documentElement.style.setProperty("--scroll-gutter", `${gutter}px`);
}

function prefersReducedMotion() {
  try {
    return !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  } catch {
    return false;
  }
}

export default function BackToTop() {
  const [scrollEl, setScrollEl] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setScrollEl(document.querySelector(".routeScroll"));
  }, []);

  useEffect(() => {
    if (!scrollEl) return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      setVisible(scrollEl.scrollTop > THRESHOLD);
      syncScrollGutter(scrollEl);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    const cancelFirstRead = afterPaint(update);
    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      cancelFirstRead();
      scrollEl.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [scrollEl]);

  const onClick = () => {
    scrollEl?.scrollTo({
      top: 0,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  };

  return (
    <button
      type="button"
      className={`backToTop${visible ? " isVisible" : ""}`}
      onClick={onClick}
      aria-label="Scroll to top"
      title="Back to top"
      tabIndex={visible ? 0 : -1}
      aria-hidden={visible ? undefined : "true"}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 14.5l6-6 6 6" />
      </svg>
    </button>
  );
}
